import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { InformationCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import {
  lockboxesApi,
  AccessCode,
  AccessCodesResponse
} from "api/landlord/lockboxes";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";
import { getApiErrorKey } from "utils/error";
import { accessCodesQueryKey } from "./queryKeys";

const VENDOR_REFUSED_MESSAGE =
  "SentriLock didn't cancel this code. It still works. Try again in a minute.";
const CANCEL_FAILED_MESSAGE =
  "We couldn't cancel this code, so it may still work. Try again in a minute.";

const STALE_CODE_MESSAGES: Record<string, string> = {
  code_already_canceled: "This code was already canceled.",
  code_expired: "This code has already expired.",
  code_not_cancelable: "This code can't be canceled."
};

export const CancelLockboxCodeDialog = ({
  lockboxId,
  code,
  onClose
}: {
  lockboxId: number;
  code: AccessCode | null;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();

  const { mutate: cancelCode, isPending } = useMutation({
    mutationKey: ["LOCKBOX", "ACCESS_CODE", "DELETE", lockboxId],
    mutationFn: (target: AccessCode) =>
      lockboxesApi.cancelAccessCode(lockboxId, target.id),
    onSuccess: (_, target) => {
      queryClient.setQueryData<AccessCodesResponse>(
        accessCodesQueryKey(lockboxId),
        (prev) =>
          prev && {
            ...prev,
            access_codes: prev.access_codes.filter(
              (item) => item.source !== target.source || item.id !== target.id
            )
          }
      );
      queryClient.invalidateQueries({ queryKey: ["LOCKBOXES"] });
      queryClient.invalidateQueries({
        queryKey: ["LOCKBOX_ACTIVITY_LOG", lockboxId]
      });
      toast.success(`Code ${target.code} canceled`);
      onClose();
    },
    onError: (err) => {
      const key = getApiErrorKey(err);
      if (key && STALE_CODE_MESSAGES[key]) {
        queryClient.invalidateQueries({
          queryKey: accessCodesQueryKey(lockboxId)
        });
        onClose();
      }
      const vendorRefused = key === "sentrilock_cancel_failed";
      toast.error(
        (key && STALE_CODE_MESSAGES[key]) ||
          (vendorRefused ? VENDOR_REFUSED_MESSAGE : CANCEL_FAILED_MESSAGE)
      );
    }
  });

  const holder = code?.recipient_name || "Anyone you shared it with";

  return (
    <ConfirmDialog
      opened={Boolean(code)}
      onClose={() => {
        if (!isPending) onClose();
      }}
      onConfirm={() => {
        if (code) cancelCode(code);
      }}
      zIndex={120}
      variant="danger"
      icon={{ icon: InformationCircleIcon, variant: "danger" }}
      title={`Cancel code ${code?.code ?? ""}?`}
      description={`It will stop working right away. ${holder} won't be able to open the lockbox.`}
      cancelButton={{ label: "Keep code", disabled: isPending }}
      confirmButton={{
        label: "Cancel code",
        loading: isPending,
        disabled: isPending
      }}
    />
  );
};
